import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, Briefcase, Target, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';

const teamMembers = {
  fr: [
    {
      id: 1,
      name: "David Aluma",
      role: "Président Directeur Général & Fondateur",
      title: "",
      image: "",
      description: "Visionnaire et passionné par le monde du bâtiment, David Aluma est le moteur et la pierre angulaire de D&J Engineering.",
      fullDescription: "Diplômé en génie civil, il possède une solide expertise en conception, estimation et dessin structurel. Il transmet également ses connaissances à la prochaine génération d'Ingénieurs en tant que professeur au Collège La Cité. Grâce à son œil analytique et son approche stratégique, il transforme chaque idée en solution concrète et efficace.\n\nSous sa direction, D&J Engineering s'est bâti une réputation de fiabilité, d'expertise et de performance, guidée par une seule ambition : élever les standards de qualité dans l'industrie du bâtiment.",
      expertise: ["Conception structurale", "Estimation", "Dessin structurel", "Leadership stratégique", "Génie civil", "Enseignement"],
      highlights: [
        { icon: Award, text: "Diplômé en génie civil" },
        { icon: Target, text: "Vision stratégique et analytique" },
        { icon: Briefcase, text: "Fondateur de D&J Engineering" }
      ]
    },
    {
      id: 2,
      name: "Joël Padingani Ing.",
      role: "Vice-Président & Associé",
      title: "Ing.",
      image: "",
      description: "Pilier stratégique de D&J Engineering, Joël Padingani Ing. est un ingénieur civil passionné et un leader naturel reconnu pour sa capacité à faire avancer les projets avec efficacité et rigueur.",
      fullDescription: "En tant que Vice-Président et Associé, il joue un rôle clé dans la planification, la gestion et la réussite des mandats de construction.\n\nFort d'une solide expérience en gestion de projet et en analyse des coûts, Joël allie méthode, précision et vision globale pour garantir que chaque projet soit livré dans les délais, dans le budget et selon les plus hauts standards de qualité.",
      expertise: ["Gestion de projet", "Analyse des coûts", "Planification", "Coordination", "Leadership"],
      highlights: [
        { icon: Award, text: "Ingénieur civil passionné" },
        { icon: Target, text: "Expert en gestion de projet" },
        { icon: Briefcase, text: "Vice-Président & Associé" }
      ]
    },
    {
      id: 3,
      name: "Léonard Ma Gloire Bila",
      role: "Consultant Principal & Expert en Construction",
      title: "",
      image: "",
      description: "Avec plus de 15 ans d'expérience dans le domaine de la construction, Léonard Ma Gloire Bila apporte à D&J Engineering une expertise rare et une vision stratégique forgée sur le terrain.",
      fullDescription: "Véritable référence dans la gestion de projet et l'analyse des coûts, il combine savoir-faire technique, rigueur et leadership inspirant.\n\nSon impressionnant parcours l'a amené à superviser de nombreux projets d'envergure — des bâtiments commerciaux aux infrastructures complexes — toujours avec une attention méticuleuse aux détails et une recherche constante de l'excellence.",
      expertise: ["15+ ans d'expérience", "Gestion de projet", "Analyse des coûts", "Supervision de chantier", "Bâtiments commerciaux"],
      highlights: [
        { icon: Award, text: "Plus de 15 ans d'expérience" },
        { icon: Target, text: "Expert en analyse des coûts" },
        { icon: Briefcase, text: "Consultant Principal" }
      ]
    },
    {
      id: 4,
      name: "Ali El-Husseini",
      role: "Consultant en ingénierie structurale",
      title: "P.Eng., ing., PE",
      image: "",
      description: "Ali est notre consultant en ingénierie structurale, ingénieur agréé en Ontario et au Québec, comptant plus de 15 années d'expérience professionnelle en analyse, conception et évaluation de structures.",
      fullDescription: "Il se spécialise dans la vérification de la capacité portante des éléments structuraux principaux existants en béton, acier et bois, en offrant des solutions techniques fiables pour les projets de rénovation, de réhabilitation et de changement d'usage. Son expertise comprend également l'évaluation des fondations superficielles et profondes existantes, à partir des dessins disponibles et des rapports géotechniques.\n\nFort d'une solide expérience tant en construction neuve qu'en structures existantes, Ali propose des solutions pratiques, conformes aux normes en vigueur et adaptées aux besoins spécifiques de chaque projet.",
      expertise: ["15+ ans d'expérience", "Analyse structurale", "Conception de structures", "Évaluation structurelle", "Béton, acier et bois", "Fondations"],
      highlights: [
        { icon: Award, text: "P.Eng., ing., PE" },
        { icon: Target, text: "Expert en structures existantes" },
        { icon: Briefcase, text: "Ingénieur agréé ON & QC" }
      ]
    }
  ],
  en: [
    {
      id: 1,
      name: "David Aluma",
      role: "CEO & Founder",
      title: "",
      image: "",
      description: "Visionary and passionate about the building industry, David Aluma is the driving force and cornerstone of D&J Engineering.",
      fullDescription: "Graduate in civil engineering, he has solid expertise in design, estimation, and structural drawing. He also shares his knowledge with the next generation of Engineers as a professor at Collège La Cité. Thanks to his analytical eye and strategic approach, he transforms every idea into a concrete and effective solution.\n\nUnder his leadership, D&J Engineering has built a reputation for reliability, expertise, and performance, guided by a single ambition: to elevate quality standards in the building industry.",
      expertise: ["Structural Design", "Estimation", "Structural Drawing", "Strategic Leadership", "Civil Engineering", "Teaching"],
      highlights: [
        { icon: Award, text: "Civil Engineering Graduate" },
        { icon: Target, text: "Strategic and Analytical Vision" },
        { icon: Briefcase, text: "Founder of D&J Engineering" }
      ]
    },
    {
      id: 2,
      name: "Joël Padingani Ing.",
      role: "Vice President & Partner",
      title: "Eng.",
      image: "",
      description: "Strategic pillar of D&J Engineering, Joël Padingani Ing. is a passionate civil engineer and natural leader recognized for his ability to advance projects with efficiency and rigor.",
      fullDescription: "As Vice President and Partner, he plays a key role in planning, management, and success of construction mandates.\n\nWith solid experience in project management and cost analysis, Joël combines method, precision, and global vision to ensure each project is delivered on time, on budget, and to the highest quality standards.",
      expertise: ["Project Management", "Cost Analysis", "Planning", "Coordination", "Leadership"],
      highlights: [
        { icon: Award, text: "Passionate Civil Engineer" },
        { icon: Target, text: "Project Management Expert" },
        { icon: Briefcase, text: "Vice President & Partner" }
      ]
    },
    {
      id: 3,
      name: "Léonard Ma Gloire Bila",
      role: "Principal Consultant & Construction Expert",
      title: "",
      image: "",
      description: "With over 15 years of experience in construction, Léonard Ma Gloire Bila brings rare expertise and strategic vision forged in the field to D&J Engineering.",
      fullDescription: "A true reference in project management and cost analysis, he combines technical know-how, rigor, and inspiring leadership.\n\nHis impressive career has led him to supervise numerous major projects — from commercial buildings to complex infrastructure — always with meticulous attention to detail and a constant pursuit of excellence.",
      expertise: ["15+ Years Experience", "Project Management", "Cost Analysis", "Site Supervision", "Commercial Buildings"],
      highlights: [
        { icon: Award, text: "Over 15 Years Experience" },
        { icon: Target, text: "Cost Analysis Expert" },
        { icon: Briefcase, text: "Principal Consultant" }
      ]
    },
    {
      id: 4,
      name: "Ali El-Husseini",
      role: "Structural Engineering Consultant",
      title: "P.Eng., ing., PE",
      image: "",
      description: "Ali is our structural engineering consultant, a licensed engineer in Ontario and Quebec, with over 15 years of professional experience in structural analysis, design, and evaluation.",
      fullDescription: "He specializes in verifying the load-bearing capacity of existing main structural elements in concrete, steel, and wood, providing reliable technical solutions for renovation, rehabilitation, and change of use projects. His expertise also includes the evaluation of existing shallow and deep foundations, based on available drawings and geotechnical reports.\n\nWith solid experience in both new construction and existing structures, Ali offers practical solutions that comply with current standards and are tailored to the specific needs of each project.",
      expertise: ["15+ Years Experience", "Structural Analysis", "Structure Design", "Structural Evaluation", "Concrete, Steel & Wood", "Foundations"],
      highlights: [
        { icon: Award, text: "P.Eng., ing., PE" },
        { icon: Target, text: "Expert in Existing Structures" },
        { icon: Briefcase, text: "Licensed Engineer ON & QC" }
      ]
    }
  ]
};

const TeamPage = () => {
  const { language } = useLanguage();
  const team = teamMembers[language] || teamMembers.fr;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, #0d9488 0%, transparent 50%)`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            <Users className="w-4 h-4 inline mr-2" />
            {language === 'fr' ? 'Notre équipe' : 'Our Team'}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? "L'équipe D&J Engineering" : "The D&J Engineering Team"}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {language === 'fr' 
              ? "Des professionnels passionnés et expérimentés, dédiés à l'excellence en ingénierie structurale."
              : "Passionate and experienced professionals dedicated to excellence in structural engineering."}
          </p>
        </div>
      </section>

      {/* Team Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
              <Users className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {language === 'fr' ? 'Une équipe polyvalente et dévouée' : 'A Versatile and Dedicated Team'}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {language === 'fr' 
                ? "Notre équipe multidisciplinaire combine expertise technique, leadership et passion pour délivrer des projets d'exception. La complémentarité de nos compétences est notre plus grande force."
                : "Our multidisciplinary team combines technical expertise, leadership, and passion to deliver exceptional projects. The complementarity of our skills is our greatest strength."}
            </p>
          </div>
        </div>
      </section>

      {/* Team Members - Detailed */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-20">
            {team.map((member, index) => {
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={member.id}
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}
                >
                  {/* Image Side */}
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative">
                      <div className="absolute -inset-4 bg-gradient-to-r from-blue-200 to-slate-200 rounded-3xl blur-2xl opacity-40" />
                      <Card className="relative bg-white border-0 shadow-2xl overflow-hidden">
                        <CardContent className="p-0">
                          <div className="aspect-[4/5] relative overflow-hidden">
                            {member.image ? (
                              <>
                                <img 
                                  src={member.image} 
                                  alt={member.name}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                              </>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-2xl">
                                  <User className="w-24 h-24 text-white" />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-slate-900 to-transparent">
                            <div className="flex items-center gap-2 text-white">
                              <span className="text-2xl font-bold">{member.name}</span>
                              {member.title && <span className="text-blue-300 font-medium">{member.title}</span>}
                            </div>
                            <p className="text-blue-300 font-medium">{member.role}</p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {/* Floating Badge */}
                      {index === 0 && (
                        <div className="absolute -top-4 -right-4 bg-blue-700 text-white px-4 py-2 rounded-xl shadow-lg">
                          <span className="font-bold">{language === 'fr' ? 'Fondateur' : 'Founder'}</span>
                        </div>
                      )}
                      {index === 2 && (
                        <div className="absolute -top-4 -right-4 bg-slate-800 text-white px-4 py-2 rounded-xl shadow-lg">
                          <span className="font-bold">15+ {language === 'fr' ? 'ans' : 'years'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Content Side */}
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="mb-6">
                      <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase">
                        {member.role}
                      </span>
                      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
                        {member.name}
                      </h2>
                    </div>
                    
                    <p className="text-lg text-slate-700 font-medium mb-4">
                      {member.description}
                    </p>
                    
                    <div className="text-slate-600 leading-relaxed mb-8 space-y-4">
                      {member.fullDescription.split('\n\n').map((para, idx) => (
                        <p key={idx}>{para}</p>
                      ))}
                    </div>
                    
                    {/* Highlights */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      {member.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <highlight.icon className="w-5 h-5 text-blue-700" />
                          </div>
                          <span className="text-sm text-slate-700 font-medium">{highlight.text}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Expertise Tags */}
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((skill, idx) => (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className="bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-800 transition-colors px-3 py-1"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {language === 'fr' 
                  ? 'Travaillons ensemble sur votre prochain projet'
                  : "Let's work together on your next project"}
              </h2>
              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                {language === 'fr'
                  ? 'Notre équipe est prête à vous accompagner dans la réalisation de vos projets de construction.'
                  : 'Our team is ready to support you in achieving your construction projects.'}
              </p>
              <Link to="/contact">
                <Button className="bg-blue-500 hover:bg-blue-400 text-white px-10 py-6 text-lg rounded-xl font-medium transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 group">
                  {language === 'fr' ? 'Contactez-nous' : 'Contact us'}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
